/**
 * ReconnectingOverlay Component
 * 
 * Displayed when the WebSocket connection drops unexpectedly and the system
 * is attempting to reconnect. Shows retry progress and offers save/cancel options.
 */

import { useTranslation } from 'react-i18next';

interface ReconnectingOverlayProps {
  retryAttempt: number;
  maxRetries: number;
  onCancel: () => void;
  onSave?: () => Promise<void>;
  isSaving?: boolean;
}

export default function ReconnectingOverlay({
  retryAttempt,
  maxRetries,
  onCancel,
  onSave,
  isSaving = false,
}: ReconnectingOverlayProps) {
  const { t } = useTranslation();

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-30 pointer-events-auto">
      <div className="text-center px-6 py-5 max-w-lg">
        <div className="inline-flex flex-col items-center gap-4 bg-black/60 backdrop-blur-md rounded-2xl px-8 py-6 border border-white/10">
          {/* Spinner */}
          <div className="relative w-12 h-12">
            <div className="absolute inset-0 border-4 border-amber-500/30 border-t-amber-500 rounded-full animate-spin"></div>
          </div>

          {/* Status text */}
          <div className="space-y-1">
            <p className="text-white text-base font-semibold">
              {t('errors.connectionLost', 'Ligação perdida')}
            </p>
            <p className="text-white/80 text-sm">
              {t('errors.reconnectingAttempt', 'A reconectar... Tentativa {{current}} de {{max}}', {
                current: retryAttempt,
                max: maxRetries,
              })}
            </p>
          </div>

          {/* Progress dots */}
          <div className="flex gap-2">
            {Array.from({ length: maxRetries }, (_, i) => (
              <div
                key={i}
                className={`w-2.5 h-2.5 rounded-full transition-colors duration-300 ${
                  i < retryAttempt
                    ? 'bg-amber-500'
                    : i === retryAttempt
                      ? 'bg-amber-500 animate-pulse'
                      : 'bg-white/20'
                }`}
              />
            ))}
          </div>

          {/* Action buttons */}
          <div className="flex flex-col gap-2 w-full mt-2">
            {onSave && (
              <button
                onClick={onSave}
                disabled={isSaving}
                className="px-6 py-2.5 bg-green-600 hover:bg-green-700 disabled:bg-green-800 disabled:opacity-60 text-white rounded-lg transition-colors text-sm font-medium"
              >
                {isSaving
                  ? t('scenario.session.saving', 'A guardar...')
                  : t('scenario.session.saveAndExit', 'Guardar e Sair')}
              </button>
            )}
            <button
              onClick={onCancel}
              className="px-6 py-2.5 bg-white/10 hover:bg-white/15 text-white rounded-lg border border-white/15 transition-colors text-sm font-medium"
            >
              {t('common.buttons.cancel', 'Cancelar')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
